/**
 * The three-agent collision scenario, as a script you can run against any {@link Coordinator}.
 *
 * Three agents build Season 2 of the league ladder demo at once:
 * - alpha (WP-21, seasons and divisions) migrates the schema and changes GET /ladder.
 * - bravo (WP-22, match disputes) migrates the schema and touches applyResult.
 * - charlie (WP-23, Elo ratings) rewrites applyResult and changes GET /ladder.
 *
 * Declared overlaps (six): alpha/bravo collide on path `demo/api/migrations/**`, contract
 * `schema:matches` and the migration flag; bravo/charlie on symbol `applyResult`; alpha/charlie on
 * path `demo/api/src/routes/ladder.ts` and contract `api:GET /ladder`. Mid-build, charlie's push edits
 * bravo's `matches.ts` without a claim, raising a `diff` overlap.
 *
 * They are settled with every resolution type: sequence (migration lane rule, agreement and yield),
 * split, shared (via a counter), escalation then a human decision, and handoff. Then alpha lands,
 * bravo fails verification once and lands on the second try, and the stack previews and promotes.
 */
import type { IntentClaim } from "../claim.ts";
import type { Coordinator, CoordinatorSnapshot } from "../coordinator.ts";
import type { PlatformEvent } from "../events.ts";
import type { LandRequest } from "../land.ts";
import { createManualClock, createMockCoordinator } from "../mock-coordinator.ts";
import type { Overlap, OverlapKind } from "../overlap.ts";
import type { Session } from "../session.ts";
import { stackResourceName, type DeployRecord } from "../stack.ts";
import { AGENTS, CLAIMS, LADDER_STACK, SEASON_2_PLAN, SHA, type AgentKey } from "./season-2.ts";

/** Where the scenario starts (Day 5 of the sprint). */
export const SCENARIO_START = "2026-10-06T00:30:00.000Z";

/** Stop points for {@link runThreeAgentCollision}, in order. */
export const SCENARIO_STAGES = ["claims", "negotiation", "diff", "complete"] as const;
/** One of {@link SCENARIO_STAGES}. */
export type ScenarioStage = (typeof SCENARIO_STAGES)[number];

/** Names for the seven overlaps the scenario raises. */
export type ScenarioOverlapKey =
  | "migrationsPath"
  | "matchesContract"
  | "migration"
  | "applyResultSymbol"
  | "ladderPath"
  | "ladderContract"
  | "matchesDiff";

/** What a run produced, so tests and harnesses can make assertions by name. */
export interface CollisionRun {
  /** Sessions by agent, as registered. */
  sessions: Record<AgentKey, Session>;
  /** Claims by key (see `CLAIMS`), as declared. */
  claims: Record<string, IntentClaim>;
  /** Overlaps by name, as first returned (later state: use `listOverlaps`). */
  overlaps: Partial<Record<ScenarioOverlapKey, Overlap>>;
  /** Land requests by name. */
  lands: Partial<Record<"alpha" | "bravo1" | "bravo2", LandRequest>>;
  /** Deploy records by name. */
  deploys: Partial<Record<"preview" | "production", DeployRecord>>;
}

/** Options for {@link runThreeAgentCollision}. */
export interface RunOptions {
  /** Called between steps to move time on (for the mock: advance a manual clock). Agents heartbeat after each tick. */
  tick?: () => void | Promise<void>;
  /** Stop after this stage. Defaults to `complete`. */
  until?: ScenarioStage;
}

function pick(list: Overlap[], kind: OverlapKind): Overlap {
  const o = list.find((x) => x.kind === kind);
  if (!o) throw new Error(`scenario expected a ${kind} overlap but the coordinator raised: ${list.map((x) => x.kind).join(", ") || "none"}`);
  return o;
}

/** Run the scenario against a coordinator. Throws if the coordinator does not behave as the contract says. */
export async function runThreeAgentCollision(c: Coordinator, options: RunOptions = {}): Promise<CollisionRun> {
  const until = SCENARIO_STAGES.indexOf(options.until ?? "complete");
  const run: CollisionRun = { sessions: {} as Record<AgentKey, Session>, claims: {}, overlaps: {}, lands: {}, deploys: {} };
  const landed = new Set<AgentKey>();
  const id = (a: AgentKey) => run.sessions[a].id;

  const step = async (note?: Partial<Record<AgentKey, string>>) => {
    await options.tick?.();
    for (const a of Object.keys(run.sessions) as AgentKey[]) {
      if (landed.has(a)) continue;
      const n = note?.[a];
      await c.heartbeat(n === undefined ? { sessionId: id(a) } : { sessionId: id(a), note: n });
    }
  };

  // Plan, approval, dispatch -------------------------------------------------
  await c.submitPlan(SEASON_2_PLAN);
  await step();
  await c.approvePlan({
    specId: SEASON_2_PLAN.specId,
    approvedBy: "human:ryan",
    answers: { "Q-1": "Freeze both players' ratings until the dispute is resolved." },
  });
  for (const a of ["alpha", "bravo", "charlie"] as const) {
    await c.dispatchPackage({ wpId: AGENTS[a].wpId, forkRepo: AGENTS[a].forkRepo });
  }
  await step();
  for (const a of ["alpha", "bravo", "charlie"] as const) run.sessions[a] = await c.registerSession(AGENTS[a]);
  await step({ alpha: "Reading the spec", bravo: "Reading the spec", charlie: "Reading the spec" });

  // Claims --------------------------------------------------------------------
  for (const claim of CLAIMS) {
    const result = await c.declareClaim({ sessionId: id(claim.agent), ...claim.input });
    run.claims[claim.key] = result.claim;
    if (claim.key === "bravo.schema") {
      run.overlaps.migrationsPath = pick(result.overlaps, "path");
      run.overlaps.matchesContract = pick(result.overlaps, "contract");
      run.overlaps.migration = pick(result.overlaps, "migration");
    }
    if (claim.key === "charlie.elo") run.overlaps.applyResultSymbol = pick(result.overlaps, "symbol");
    if (claim.key === "charlie.ladder") {
      run.overlaps.ladderPath = pick(result.overlaps, "path");
      run.overlaps.ladderContract = pick(result.overlaps, "contract");
    }
    await step();
  }
  if (until === 0) return run;

  // Negotiation ---------------------------------------------------------------
  const o = run.overlaps as Record<ScenarioOverlapKey, Overlap>;

  // Migrations path: bravo proposes alpha goes first (matching the lane); alpha accepts.
  const seq = await c.postMessage({
    from: id("bravo"),
    to: id("alpha"),
    overlapId: o.migrationsPath.id,
    type: "propose_sequence",
    body: "You hold the migration lane, so land your seasons migration first; I'll rebase my disputes migration on it.",
    proposal: {
      outcome: "sequence",
      terms: { summary: "alpha's migration lands first; bravo rebases", order: [id("alpha"), id("bravo")] },
    },
  });
  await step();
  await c.postMessage({
    from: id("alpha"),
    to: id("bravo"),
    overlapId: o.migrationsPath.id,
    type: "accept",
    inReplyTo: seq.id,
    body: "Agreed. My migration is 0003_seasons.sql; take 0004.",
  });
  await step();

  // schema:matches: bravo yields, alpha's season_id column goes in first.
  await c.postMessage({
    from: id("bravo"),
    to: id("alpha"),
    overlapId: o.matchesContract.id,
    type: "yield",
    body: "Your season_id column is a prerequisite for per-season disputes. You change matches first.",
  });
  await step();

  // applyResult: bravo proposes a split, charlie owns applyResult and calls bravo's isDisputed().
  const bravoNarrowed = { ...run.claims["bravo.disputes"]!.scope, symbols: [] };
  const split = await c.postMessage({
    from: id("bravo"),
    to: id("charlie"),
    overlapId: o.applyResultSymbol.id,
    type: "propose_split",
    body: "You own applyResult. I'll export isDisputed(match) from validation.ts and you call it before applying a result.",
    proposal: {
      outcome: "split",
      terms: { summary: "charlie owns applyResult; bravo provides isDisputed() in validation.ts", scopes: [{ sessionId: id("bravo"), scope: bravoNarrowed }] },
    },
  });
  await step();
  await c.postMessage({
    from: id("charlie"),
    to: id("bravo"),
    overlapId: o.applyResultSymbol.id,
    type: "accept",
    inReplyTo: split.id,
    body: "Works for me. I'll call isDisputed() first thing in applyResult.",
  });
  await step();

  // ladder.ts path: alpha proposes sequence, charlie counters with shared, alpha accepts.
  const ladderSeq = await c.postMessage({
    from: id("alpha"),
    to: id("charlie"),
    overlapId: o.ladderPath.id,
    type: "propose_sequence",
    body: "I'm restructuring ladder.ts for seasons. Can you wait for me to land?",
    proposal: { outcome: "sequence", terms: { summary: "alpha lands ladder.ts first", order: [id("alpha"), id("charlie")] } },
  });
  await step();
  const counter = await c.postMessage({
    from: id("charlie"),
    to: id("alpha"),
    overlapId: o.ladderPath.id,
    type: "counter",
    inReplyTo: ladderSeq.id,
    body: "My change is one field in the row mapper. Let's share the file: you own the query, I only touch toRow().",
    proposal: { outcome: "shared", terms: { summary: "alpha owns the ladder query; charlie only edits toRow()" } },
  });
  await step();
  await c.postMessage({
    from: id("alpha"),
    to: id("charlie"),
    overlapId: o.ladderPath.id,
    type: "accept",
    inReplyTo: counter.id,
    body: "Deal. Keep to toRow() and we won't conflict.",
  });
  await step();

  // GET /ladder contract: alpha wants charlie to hand it over, charlie refuses, the protocol escalates, a human decides.
  const handoff = await c.postMessage({
    from: id("alpha"),
    to: id("charlie"),
    overlapId: o.ladderContract.id,
    type: "propose_handoff",
    body: "Hand the GET /ladder response shape to me; I'll add your rating field while I'm in there.",
    proposal: { outcome: "handoff", terms: { summary: "charlie hands GET /ladder to alpha", from: id("charlie"), to: id("alpha") } },
  });
  await step();
  await c.postMessage({
    from: id("charlie"),
    to: id("alpha"),
    overlapId: o.ladderContract.id,
    type: "reject",
    inReplyTo: handoff.id,
    body: "The rating field is the core of my package's acceptance criteria. I need to own it.",
  });
  await step();
  await c.recordResolution({
    overlapId: o.ladderContract.id,
    outcome: "escalated",
    terms: {
      summary: "alpha and charlie both need to change the GET /ladder response",
      positions: [
        { sessionId: id("alpha"), position: "alpha should own the response shape and add rating for charlie" },
        { sessionId: id("charlie"), position: "charlie must add rating itself; it is charlie's acceptance criterion" },
      ],
    },
    agreedBy: ["system:protocol"],
    via: "rule",
    reason: "A handoff was rejected with no counter; contract deadlocks escalate.",
  });
  await step();
  await c.recordResolution({
    overlapId: o.ladderContract.id,
    outcome: "sequence",
    terms: { summary: "alpha lands GET /ladder?season= first; charlie then adds rating", order: [id("alpha"), id("charlie")] },
    agreedBy: ["human:ryan"],
    via: "human",
    reason: "Seasons change the response structure; rating is additive and rebases cleanly on top.",
  });
  await step({ alpha: "Writing 0003_seasons.sql", bravo: "Dispute endpoint", charlie: "Elo maths" });
  if (until === 1) return run;

  // Mid-build diff ------------------------------------------------------------
  const charlieDiff = await c.ingestDiffSummary({
    sessionId: id("charlie"),
    commit: SHA.charlie1,
    baseCommit: SHA.base,
    files: [
      { path: "demo/jobs/src/elo.ts", change: "added", hunks: [{ oldStart: 0, oldLines: 0, newStart: 1, newLines: 48 }], symbols: ["demo/jobs/src/elo.ts#expectedScore"], contracts: [] },
      { path: "demo/api/src/lib/ladder.ts", change: "modified", hunks: [{ oldStart: 12, oldLines: 20, newStart: 12, newLines: 31 }], symbols: ["demo/api/src/lib/ladder.ts#applyResult"], contracts: [] },
      { path: "demo/api/src/routes/matches.ts", change: "modified", hunks: [{ oldStart: 40, oldLines: 2, newStart: 40, newLines: 6 }], symbols: [], contracts: [] },
    ],
    createdAt: new Date(Date.parse(SCENARIO_START) + 60 * 60_000).toISOString(),
  });
  run.overlaps.matchesDiff = pick(charlieDiff.overlaps, "diff");
  await step();
  const diffHandoff = await c.postMessage({
    from: id("charlie"),
    to: id("bravo"),
    overlapId: o.matchesDiff.id,
    type: "propose_handoff",
    body: "I added a recalculation trigger to the results route, which is yours. Can you take it? It's a 4 line call to enqueueRecalc().",
    proposal: { outcome: "handoff", terms: { summary: "bravo takes the recalculation trigger in matches.ts", from: id("charlie"), to: id("bravo") } },
  });
  await step();
  await c.postMessage({
    from: id("bravo"),
    to: id("charlie"),
    overlapId: o.matchesDiff.id,
    type: "accept",
    inReplyTo: diffHandoff.id,
    body: "Taking it. Drop it from your branch and I'll add it with the dispute changes.",
  });
  await step({ charlie: "Removed matches.ts change; Elo job next" });
  if (until === 2) return run;

  // Verification and landing --------------------------------------------------
  await c.ingestDiffSummary({
    sessionId: id("alpha"),
    commit: SHA.alpha1,
    baseCommit: SHA.base,
    files: [
      { path: "demo/api/migrations/0003_seasons.sql", change: "added", hunks: [{ oldStart: 0, oldLines: 0, newStart: 1, newLines: 22 }], symbols: [], contracts: ["schema:seasons", "schema:matches"] },
      { path: "demo/api/src/routes/ladder.ts", change: "modified", hunks: [{ oldStart: 5, oldLines: 14, newStart: 5, newLines: 30 }], symbols: [], contracts: ["api:GET /ladder"] },
    ],
    createdAt: new Date(Date.parse(SCENARIO_START) + 90 * 60_000).toISOString(),
  });
  await step();
  await c.ingestVerdict({
    wpId: "WP-21",
    commit: SHA.alpha1,
    pass: true,
    results: [
      { criterionId: "AC-1", met: true, evidence: [{ kind: "file", ref: "demo/api/migrations/0003_seasons.sql", line: 1, detail: "creates seasons, divisions; adds matches.season_id" }] },
      { criterionId: "AC-2", met: true, evidence: [{ kind: "test", ref: "ladder.season.test.ts > groups by division", detail: "passed" }] },
    ],
    regressions: [],
    notes: "",
    verifiedBy: "system:verifier",
    createdAt: new Date(Date.parse(SCENARIO_START) + 95 * 60_000).toISOString(),
  });
  run.lands.alpha = await c.requestLand({ sessionId: id("alpha"), commit: SHA.alpha1 });
  await step();
  run.lands.alpha = await c.reportLandResult({ landId: run.lands.alpha.id, outcome: "merged", mergeCommit: SHA.mergeAlpha });
  landed.add("alpha");
  await step();

  // bravo: first attempt fails AC-2, second passes.
  run.lands.bravo1 = await c.requestLand({ sessionId: id("bravo"), commit: SHA.bravo1 });
  await c.ingestVerdict({
    wpId: "WP-22",
    commit: SHA.bravo1,
    pass: false,
    results: [
      { criterionId: "AC-1", met: true, evidence: [{ kind: "test", ref: "matches.dispute.test.ts > returns 201", detail: "passed" }] },
      { criterionId: "AC-2", met: false, evidence: [{ kind: "note", ref: "demo/api/src/lib/ladder.ts", detail: "isDisputed() is exported but never called; disputed results still count" }] },
    ],
    regressions: [],
    notes: "Call isDisputed() from applyResult (agreed with charlie in the split) or filter disputed matches before calling it.",
    verifiedBy: "system:verifier",
    createdAt: new Date(Date.parse(SCENARIO_START) + 110 * 60_000).toISOString(),
  });
  await step({ bravo: "Fixing AC-2" });
  await c.ingestVerdict({
    wpId: "WP-22",
    commit: SHA.bravo2,
    pass: true,
    results: [
      { criterionId: "AC-1", met: true, evidence: [{ kind: "test", ref: "matches.dispute.test.ts > returns 201", detail: "passed" }] },
      { criterionId: "AC-2", met: true, evidence: [{ kind: "test", ref: "ladder.disputes.test.ts > skips disputed", detail: "passed" }] },
    ],
    regressions: [],
    notes: "",
    verifiedBy: "system:verifier",
    createdAt: new Date(Date.parse(SCENARIO_START) + 120 * 60_000).toISOString(),
  });
  run.lands.bravo2 = await c.requestLand({ sessionId: id("bravo"), commit: SHA.bravo2 });
  await step();

  // Preview bravo's branch, approve, promote, then merge.
  const branch = "wp-22";
  const name = (resource: string) => stackResourceName(LADDER_STACK, { kind: "preview", branch, resource });
  const startedAt = new Date(Date.parse(SCENARIO_START) + 125 * 60_000).toISOString();
  const finishedAt = new Date(Date.parse(SCENARIO_START) + 127 * 60_000).toISOString();
  run.deploys.preview = await c.recordDeploy({
    id: "dep_wp22_preview",
    stack: LADDER_STACK.name,
    branch,
    kind: "preview",
    commit: SHA.bravo2,
    status: "succeeded",
    resources: [
      ...LADDER_STACK.workers.map((w) => ({ type: "worker" as const, logicalName: w.name, name: name(w.name) })),
      { type: "d1", logicalName: "ladder", name: name("ladder"), cloudflareId: "d1-preview-wp22" },
      { type: "r2", logicalName: "avatars", name: name("avatars") },
    ],
    urls: [
      { worker: "api", url: `https://${name("api")}.example.workers.dev` },
      { worker: "web", url: `https://${name("web")}.example.workers.dev` },
    ],
    migrationsApplied: [{ database: "ladder", migrations: ["0001_init.sql", "0002_results.sql", "0003_seasons.sql", "0004_disputes.sql"] }],
    logsPointer: "r2://deploy-logs/dep_wp22_preview.log",
    approvedBy: null,
    approvedAt: null,
    startedAt,
    finishedAt,
  });
  await step();
  run.deploys.preview = await c.approvePromotion({ deployId: "dep_wp22_preview", approvedBy: "human:ryan" });
  run.lands.bravo2 = await c.reportLandResult({ landId: run.lands.bravo2.id, outcome: "merged", mergeCommit: SHA.mergeBravo });
  landed.add("bravo");
  await step();
  run.deploys.production = await c.recordDeploy({
    id: "dep_main_production",
    stack: LADDER_STACK.name,
    branch: "main",
    kind: "production",
    commit: SHA.mergeBravo,
    status: "succeeded",
    resources: LADDER_STACK.workers.map((w) => ({
      type: "worker" as const,
      logicalName: w.name,
      name: stackResourceName(LADDER_STACK, { kind: "production", branch: "main", resource: w.name }),
    })),
    urls: [{ worker: "web", url: "https://ladder.example.dev" }],
    migrationsApplied: [{ database: "ladder", migrations: ["0003_seasons.sql", "0004_disputes.sql"] }],
    logsPointer: "r2://deploy-logs/dep_main_production.log",
    approvedBy: null,
    approvedAt: null,
    startedAt: new Date(Date.parse(SCENARIO_START) + 130 * 60_000).toISOString(),
    finishedAt: new Date(Date.parse(SCENARIO_START) + 133 * 60_000).toISOString(),
  });
  await step({ charlie: "Rebasing on alpha's ladder changes" });
  return run;
}

/**
 * Run the scenario on a fresh mock coordinator with a manual clock (one minute per step) and return the
 * run, the full event log and the final snapshot. Deterministic: same output every time.
 * The event log is what the dashboard replays.
 */
export async function buildThreeAgentCollisionLog(until: ScenarioStage = "complete"): Promise<{
  run: CollisionRun;
  events: PlatformEvent[];
  snapshot: CoordinatorSnapshot;
}> {
  const clock = createManualClock(SCENARIO_START);
  const coordinator = createMockCoordinator({ now: clock.now });
  const run = await runThreeAgentCollision(coordinator, { tick: () => clock.advance(60_000), until });
  return { run, events: coordinator.events(), snapshot: await coordinator.snapshot() };
}
